import { Check, ChevronDown, Loader, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export const COLOUR_PALETTE = [
    { bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200',   dot: '#2563eb' },
    { bg: 'bg-gray-100',  text: 'text-gray-700',   border: 'border-gray-200',   dot: '#6b7280' },
    { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: '#7c3aed' },
    { bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200',  dot: '#b45309' },
    { bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',    dot: '#b91c1c' },
    { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: '#9a3412' },
    { bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200',  dot: '#15803d' },
    { bg: 'bg-teal-50',   text: 'text-teal-700',   border: 'border-teal-200',   dot: '#0f766e' },
];

export const getInternalStatusColor = (index) => {
    if (index === undefined || index === null) return COLOUR_PALETTE[0];
    const numIndex = Number(index);
    return COLOUR_PALETTE[numIndex] ?? COLOUR_PALETTE[COLOUR_PALETTE.length - 1];
};

export const maskPaypal = (email) => {
  if (!email || !email.includes("@")) return "Email not set";
  const username = email.split("@")[0];
  const masked = username.slice(0, 5) + "****";
  return masked;
};

export function encodeToBase64(str) {
  if (!str) return "";
  return btoa(str);
}
// Add this to your utils file alongside encodeToBase64
export function decodeFromBase64(str) {
  if (!str) return "";
  try {
    return atob(str);
  } catch (error) {
    console.error("Base64 decoding failed:", error);
    return str; // fallback to original string
  }
}
export function getOrderStatusText(status, coreSettings = null) {
  // Use coreSettings.ORDER_STATUS if available
  if (coreSettings?.ORDER_STATUS) {
    return coreSettings.ORDER_STATUS[status] || "Unknown";
  }

  // Fallback to hardcoded values when coreSettings is not loaded
  switch (status) {
    case 0:
      return "Order Placed";
    case 1:
      return "Order Confirmed";
    case 2:
      return "Order Processed";
    case 3:
      return "Packed for Delivery";
    case 4:
      return "Ready for Pickup";
    case 5:
      return "Order Delivered";
    case 98:
      return "Cancelled";
    default:
      return "Unknown";
  }
}
export function getStatusColor(status) {
  const statusNum = parseInt(status);

  switch (statusNum) {
    case 0: // Order Placed
      return "text-blue-800";
    case 1: // Order Confirmed
      return "text-black-800";
    case 2: // Order Processed
      return "bg-indigo-100 text-indigo-800";
    case 3: // Packed for Delivery
      return "bg-purple-100 text-purple-800";
    case 4: // Ready for Pickup
      return "bg-orange-100 text-orange-800";
    case 5: // Order Delivered
      return "bg-green-100 text-green-800";
    case 98: // Cancelled
      return "bg-red-100 text-red-800";
    default: // Unknown
      return "bg-gray-100 text-gray-800";
  }
}

// Generate all status options including Cancelled from your function
export function getOrderStatusOptions(coreSettings = null) {
  // If coreSettings.ORDER_STATUS is available, use it
  if (coreSettings?.ORDER_STATUS) {
    return Object.entries(coreSettings.ORDER_STATUS).map(([code, label]) => ({
      value: code.toString(),
      label: label,
    }));
  }

  // Fallback to hardcoded status codes
  const statusCodes = [0, 1, 2, 3, 4, 5, 98];

  return statusCodes.map((code) => ({
    value: code.toString(),
    label: getOrderStatusText(code),
  }));
}

export function formatPaymentMethod(paymentType, orderData = null, isAdmin = false) {
  if (!paymentType) return "-";
  
  // Check if fully paid using rewards
  const totalPaid = orderData?.payment_info ? orderData.payment_info.total_paid : orderData?.total_paid;
  
  // Get reward amount from rewards_info (detail page) or others_info (list page)
  const appliedRewards = orderData?.rewards_info?.applied_reward_amount || 
                         (orderData?.others_info?.rewards ? parseFloat(orderData.others_info.rewards.redeemed_rewards_amount || 0) : 0);

  if (totalPaid === 0 && appliedRewards > 0) {
    return isAdmin ? "Cash ( Rewards)" : "Rewards";
  }

  return paymentType
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function formatPhoneNumber(phone) {
  if (!phone) return "";
  // Remove all non-digits and format as (XXX) XXX-XXXX
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length >= 10) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
  }
  return phone;
}

export function formatPrice(amount) {
  if (!amount && amount !== 0) return "";

  // Convert to number if it's a string
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;

  // Check if it's a valid number
  if (isNaN(numAmount)) return "";

  // Format as US currency
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount);
}

// Function to get current week's Sunday to Saturday
export function getCurrentWeekRange() {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.

  // Calculate Sunday of current week
  const sunday = new Date(today);
  sunday.setDate(today.getDate() - dayOfWeek);

  // Calculate Saturday of current week
  const saturday = new Date(sunday);
  saturday.setDate(sunday.getDate() + 6);

  return {
    startDate: sunday.toISOString().split("T")[0], // Format: YYYY-MM-DD
    endDate: saturday.toISOString().split("T")[0],
  };
}

// Function to get active order status keys (all statuses except cancelled 98 & 99)
export function getActiveStatusKeys(coreSettings) {
  if (!coreSettings?.ORDER_STATUS) return [];
  return Object.keys(coreSettings.ORDER_STATUS).filter(
    (key) => String(key) !== '98' && String(key) !== '99'
  );
}

// Custom Dropdown Component
export function CustomDropdown({
  value,
  onChange,
  options,
  placeholder,
  className,
  disabled = false,
  loading = false,
  isMulti = false,
}) {
  const [isOpen, setIsOpen] = useState(false);

  const isSelected = (optionValue) => {
    if (isMulti) {
      return Array.isArray(value) ? value.includes(optionValue) : false;
    }
    return value === optionValue;
  };

  const getButtonLabel = () => {
    if (isMulti) {
      if (!Array.isArray(value) || value.length === 0) {
        return placeholder;
      }
      if (value.includes('')) {
        const allOption = options.find((o) => o.value === '');
        return allOption ? allOption.label : placeholder;
      }
      const selectedLabels = options
        .filter((o) => value.includes(o.value) && o.value !== '')
        .map((o) => o.label);
      return selectedLabels.length > 0 ? selectedLabels.join(', ') : placeholder;
    }
    const selectedOption = options.find((option) => option.value === value);
    return selectedOption ? selectedOption.label : placeholder;
  };

  const handleSelect = (optionValue) => {
    if (disabled || loading) return;

    if (isMulti) {
      let newValue = [];
      const currentValues = Array.isArray(value) ? value : [];

      if (optionValue === '') {
        newValue = [''];
        setIsOpen(false);
      } else {
        const valuesWithoutEmpty = currentValues.filter((v) => v !== '');
        if (valuesWithoutEmpty.includes(optionValue)) {
          newValue = valuesWithoutEmpty.filter((v) => v !== optionValue);
        } else {
          newValue = [...valuesWithoutEmpty, optionValue];
        }
        if (newValue.length === 0) {
          newValue = [''];
        }
      }
      onChange(newValue);
    } else {
      onChange(optionValue);
      setIsOpen(false);
    }
  };

  const handleToggle = () => {
    if (disabled || loading) return;
    setIsOpen(!isOpen);
  };

  const getColorClasses = (color, isSel) => {
    if (!color) return "";

    const colorMap = {
      blue: isSel
        ? "bg-blue-50 text-blue-600"
        : "text-blue-600 hover:bg-blue-50",
      red: isSel
        ? "bg-red-50 text-red-600"
        : "text-red-600 hover:bg-red-50",
      green: isSel
        ? "bg-green-50 text-green-600"
        : "text-green-600 hover:bg-green-50",
      gray: isSel
        ? "bg-gray-50 text-gray-600"
        : "text-gray-600 hover:bg-gray-50",
    };

    if (color === "#9a3412") {
      return isSel
        ? "bg-orange-50 text-[#9a3412]"
        : "text-[#9a3412] hover:bg-orange-50";
    }

    return colorMap[color] || "";
  };

  const hasSelectedValue = isMulti 
    ? (Array.isArray(value) && value.length > 0 && !value.includes(''))
    : !!value;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled || loading}
        className={`w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-left text-sm focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-transparent hover:border-gray-400 transition-colors ${
          disabled || loading ? "opacity-50 cursor-not-allowed" : ""
        } ${className}`}
      >
        <div className="flex items-center justify-between w-full min-w-0">
          <span className={`block truncate text-left mr-2 ${hasSelectedValue ? "text-gray-900 font-medium" : "text-gray-500"}`} style={{ flex: 1, minWidth: 0 }}>
            {getButtonLabel()}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            {hasSelectedValue && !disabled && !loading && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(isMulti ? [''] : '');
                }}
                className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer flex items-center justify-center"
                title="Clear selection"
              >
                <X className="w-3.5 h-3.5" />
              </span>
            )}
            {loading ? (
              <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
            ) : (
              <ChevronDown
                className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            )}
          </div>
        </div>
      </button>

      {isOpen && !loading && !disabled && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Menu */}
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
            {isMulti && hasSelectedValue && (
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50 text-xs">
                <span className="text-gray-500 font-medium">
                  Selected: {Array.isArray(value) ? value.filter((v) => v !== '').length : 0}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(['']);
                  }}
                  className="text-red-600 hover:text-red-800 font-semibold hover:underline cursor-pointer"
                >
                  Clear All
                </button>
              </div>
            )}
            {options.map((option, index) => {
              const isSel = isSelected(option.value);
              return (
                <div key={option.value}>
                  {/* Show divider before this option if divider is true */}
                  {option.divider && index > 0 && (
                    <div className="border-t border-gray-300 my-1"></div>
                  )}
                  <button
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    className={`w-full px-4 py-2 text-left text-sm focus:outline-none first:rounded-t-lg last:rounded-b-lg ${
                      isSel
                        ? getColorClasses(option.color, true) ||
                          "bg-red-50 text-red-600"
                        : getColorClasses(option.color, false) ||
                          "text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{option.label}</span>
                      {isSel && <Check className="w-4 h-4" />}
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// Format order type for display
export function formatOrderTypeForDisplay(type) {
  switch (type) {
    case "default":
      return "Default";
    case "delivery-partner":
      return "Delivery Partner";
    case "qurbani":
      return "Qurbani";
    default:
      return "Default";
  }
}

// Delivery Status Options for Order Tracking
export const deliverystatusOptions = [
  {
    value: "in-trnasit",
    label: "In Transit",
  },

  {
    value: "delivered",
    label: "Delivered",
  },
];

// Format date with optional time — always displayed in PST (America/Los_Angeles)
export function formatToUSDate(dateInput, includeTime = false, timeZone = 'America/Los_Angeles') {
  if (!dateInput) return "";
  let date;
  if (
    typeof dateInput === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(dateInput) &&
    !includeTime
  ) {
    // Date-only string — treat as local calendar date (no timezone conversion needed)
    const [year, month, day] = dateInput.split("-");
    date = new Date(year, parseInt(month) - 1, parseInt(day));
  } else {
    date = new Date(dateInput);
  }
  if (isNaN(date.getTime())) {
    return "";
  }
  const options = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone: includeTime ? timeZone : undefined,
  };
  if (includeTime) {
    options.hour = "2-digit";
    options.minute = "2-digit";
    options.hour12 = true;
  }
  return new Intl.DateTimeFormat("en-US", options).format(date);
}


// Get formatted billing address from user data
export function getBillingAddress(userData) {
  if (!userData) return "Address not available";

  let billingAddress = "";

  // Prioritize billing_address field if available and not empty
  if (userData.billing_address && userData.billing_address.trim()) {
    billingAddress = userData.billing_address.trim();
  } else {
    // Build address from individual components
    const addressParts = [
      userData.street_name,
      userData.city,
      userData.state,
      userData.zip_code || userData.postal_code, // Include zip if available
    ].filter((part) => part && part.trim());

    if (addressParts.length > 0) {
      billingAddress = addressParts.join(", ");
    } else if (
      (userData.user_role === 6 || userData.roles === 6 || userData.role === 6) &&
      (userData.payment_option === "cash" || !userData.payment_option || userData.payment_option === "")
    ) {
      billingAddress = "Not required for Cash Order";
    } else {
      billingAddress = "Please enter billing address.";
    }
  }

  return billingAddress;
}

// Enhanced Session Storage Functions with Security and Expiry
export function setSessionDataWithExpiry(key, data, ttl = 3600000) {
  // 1 hour default
  try {
    const item = {
      value: data,
      expiry: Date.now() + ttl,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(key, JSON.stringify(item));
    //console.log(`Data saved to session storage with key: ${key}, expiry: ${new Date(item.expiry).toLocaleString()}`);
    return true;
  } catch (error) {
    //console.error(`Failed to save data to session storage with key: ${key}`, error);
    return false;
  }
}

export function getSessionDataWithValidation(key) {
  try {
    const item = sessionStorage.getItem(key);
    if (!item) return null;

    const parsed = JSON.parse(item);
    // Check if data has expired
    if (Date.now() > parsed.expiry) {
      sessionStorage.removeItem(key);
      //console.log(`Session data with key: ${key} expired and removed`);
      return null;
    }

    return parsed.value;
  } catch (error) {
    console.error(`Error parsing session data with key: ${key}`, error);
    sessionStorage.removeItem(key);
    return null;
  }
}

export function clearSessionData(key) {
  try {
    sessionStorage.removeItem(key);
    //console.log(`Session data with key: ${key} cleared`);
    return true;
  } catch (error) {
    console.error(`Failed to clear session data with key: ${key}`, error);
    return false;
  }
}

export function clearAllSessionData(keys = []) {
  try {
    if (keys.length === 0) {
      sessionStorage.clear();
      //console.log('All session storage cleared');
    } else {
      keys.forEach((key) => {
        sessionStorage.removeItem(key);
      });
      //console.log(`Session data cleared for keys: ${keys.join(', ')}`);
    }
    return true;
  } catch (error) {
    console.error("Failed to clear session data", error);
    return false;
  }
}

// Helper function to get modification title
export function getModificationTitle(type) {
  switch (type) {
    case "created":
      return "Subscription Created";
    case "paused":
      return "Subscription Paused";
    case "resumed":
      return "Subscription Resumed";
    case "cancelled":
      return "Subscription Cancelled";
    case "payment_changed":
      return "Payment Method Changed";
    case "delivery_changed":
      return "Delivery Information Changed";
    default:
      return "Subscription Updated";
  }
}
// Convert category name to URL-friendly alias format (kebab-case)
export function convertToAlias(text) {
  if (!text) return "";
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "") // Remove special characters
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-"); // Replace multiple hyphens with single hyphen
}

export const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    borderRadius: "0",
    borderTop: "none",
    borderLeft: "none",
    borderRight: "none",
    borderBottom: "1px solid #d1d5db",
    boxShadow: "none",
    padding: "0.5rem 0.75rem 0.5rem 0",
    minHeight: "42px",
    height: "42px",
    "&:hover": {
      borderBottom: "1px solid #9ca3af",
    },
    ...(state.isFocused && {
      borderBottom: "1px solid #6b7280",
    }),
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0",
    height: "100%",
    display: "flex",
    alignItems: "center",
  }),
  input: (provided, state) => ({
    ...provided,
    margin: "0",
    padding: "0",
    caretColor: state.selectProps.menuIsOpen ? "auto" : "transparent",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "0 4px",
    color: "#6b7280",
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "0.5rem",
    marginTop: "0.25rem",
    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
    zIndex: 50,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: "0",
    maxHeight: "300px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#ef4444"
      : state.isFocused
      ? "#fee2e2"
      : "white",
    color: state.isSelected ? "white" : "#374151",
    padding: "0.75rem",
    cursor: "pointer",
    fontSize: "0.875rem",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "0.875rem",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#111827",
    fontSize: "0.875rem",
  }),
  groupHeading: (provided) => ({
    ...provided,
    backgroundColor: "#aa2526",
    color: "#ffffff",
    fontWeight: "600",
    fontSize: "0.875rem",
    padding: "0.5rem 0.75rem",
    margin: "0",
    textTransform: "none",
    position: "sticky",
    top: 0,
    zIndex: 1,
  }),
};

export function isFutureDate(dateString) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const selectedDateObj = new Date(dateString);
  return selectedDateObj > today;
}

export const formatTimeRange = (timeStr) => {
    if (!timeStr) return '';
    const [hoursStr, minutesStr] = timeStr.split(':');
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    const formatAmPm = (h, m) => {
        const ampm = h >= 12 ? 'pm' : 'am';
        const displayH = h % 12 || 12;
        const displayM = m.toString().padStart(2, '0');
        const displayHStr = displayH.toString().padStart(2, '0');
        return `${displayHStr}:${displayM} ${ampm}`;
    };

    const startTime = formatAmPm(hours, minutes);
    const endTime = formatAmPm(hours + 1, minutes);

    return `${startTime}-${endTime}`;
};
